/**
 * File: Careers.jsx
 * Path: /src/pages/Careers.jsx
 * Description: Careers page showcasing job opportunities and company culture at Veritect.ai
 * Key Features: Job listings, company benefits, employee testimonials, and culture highlights
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom'; // Keep Link import even if Button handles it, good practice
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ModernCtaSection from '../components/common/ModernCtaSection';
// Removed ModernStatsSection import as the corresponding section was removed (it was in the stray code block)
import useIntersectionObserver from '../hooks/useIntersectionObserver';

const JobListing = ({ title, location, type, department, isNew }) => {
  // Generate a simple slug for the URL
  const jobSlug = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  return (
    <Card
      interactive
      className="transition-all duration-300 hover:shadow-lg p-6"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-bold">{title}</h3>
            {isNew && (
              <span className="px-2 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full">
                New
              </span>
            )}
          </div>
          <div className="flex flex-wrap text-sm text-dark-500 gap-x-6 gap-y-1">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>{location}</span>
            </div>
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>{department}</span>
            </div>
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{type}</span>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          className="whitespace-nowrap"
          // Assuming Button component can handle 'to' prop for internal routing
          to={`/careers/${jobSlug}`}
        >
          View Position
        </Button>
      </div>
    </Card>
  );
};

const ValueCard = ({ icon, title, description }) => {
  return (
    <Card className="h-full flex flex-col items-start p-6"> {/* Added padding */}
      <div className="rounded-lg p-3 bg-primary-50 text-primary-600 mb-5 inline-block"> {/* Ensure icon bg doesn't stretch */}
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-3">{title}</h3>
      <p className="text-dark-500">{description}</p>
    </Card>
  );
};

const BenefitItem = ({ icon, title, description }) => {
  return (
    <div className="flex items-start">
      <div className="mr-4 text-primary-600 mt-1 flex-shrink-0"> {/* Added flex-shrink-0 */}
        {icon}
      </div>
      <div>
        <h4 className="font-semibold text-lg mb-1">{title}</h4>
        <p className="text-dark-500">{description}</p>
      </div>
    </div>
  );
};

const Careers = () => {
  // Initialize observer for scroll animations
  // Using a single observer instance for potentially multiple sections
  const [sectionRef1, isVisible1] = useIntersectionObserver({ threshold: 0.1, triggerOnce: true });
  const [sectionRef2, isVisible2] = useIntersectionObserver({ threshold: 0.1, triggerOnce: true });
  const [sectionRef3, isVisible3] = useIntersectionObserver({ threshold: 0.1, triggerOnce: true });
  const [sectionRef4, isVisible4] = useIntersectionObserver({ threshold: 0.1, triggerOnce: true });
  const [sectionRef5, isVisible5] = useIntersectionObserver({ threshold: 0.1, triggerOnce: true });


  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Current job openings data
  const jobOpenings = [
    {
      title: "Senior AI Research Scientist",
      location: "Singapore (Remote)",
      type: "Full-time",
      department: "AI Research",
      isNew: true
    },
    {
      title: "Full Stack Developer",
      location: "Singapore / Remote",
      type: "Full-time",
      department: "Engineering",
      isNew: true
    },
    {
      title: "Legal Domain Expert",
      location: "Singapore",
      type: "Full-time",
      department: "Product",
      isNew: false
    },
    {
      title: "UX/UI Designer",
      location: "Remote",
      type: "Full-time",
      department: "Design",
      isNew: false
    },
    {
      title: "Growth Marketing Manager",
      location: "Singapore",
      type: "Full-time",
      department: "Marketing",
      isNew: true
    },
    {
      title: "Customer Success Specialist",
      location: "Singapore / Remote",
      type: "Full-time",
      department: "Customer Success",
      isNew: false
    }
  ];

  // Company values
  const values = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: "Innovation",
      description: "We push the boundaries of legal technology, combining cutting-edge AI with deep domain expertise to create transformative solutions."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Efficiency",
      description: "We value productivity and intelligent solutions that save time and reduce complexity, both for our clients and in our own operations."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: "Collaboration",
      description: "We thrive on diverse perspectives, open communication, and cross-functional teamwork to solve complex challenges together."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
        </svg>
      ),
      title: "Integrity",
      description: "We uphold the highest ethical standards in our technology, business practices, and treatment of sensitive legal information."
    }
  ];

  // Benefits
  const benefits = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Competitive Compensation",
      description: "We offer attractive salaries, equity options, and performance bonuses to reward your contribution to our growth."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: "Comprehensive Health Benefits",
      description: "Enjoy full medical, dental, and vision coverage for you and your dependents, plus mental wellness support."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: "Flexible Work Arrangements",
      description: "Work remotely, set flexible hours, and enjoy unlimited paid time off with our results-oriented approach."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      title: "Beautiful Workspaces",
      description: "Access to our well-designed offices in Singapore with the latest technology and amenities when you need collaboration."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      title: "Continuous Learning",
      description: "Benefit from generous professional development budgets, conference attendance, and courses to grow your skills."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
        </svg>
      ),
      title: "Team Events & Celebrations",
      description: "Enjoy regular team-building activities, celebrations, and retreats to foster connections and recharge."
    }
  ];

  const applicationProcess = [
    {
      step: "01",
      title: "Application",
      description: "Submit your resume and a brief introduction telling us why you're interested in joining Veritect.ai."
    },
    {
      step: "02",
      title: "Initial Conversation",
      description: "Chat with our recruitment team to discuss your background, interests, and how your skills align with our needs."
    },
    {
      step: "03",
      title: "Technical Assessment",
      description: "Complete a take-home challenge or live technical discussion relevant to the role you're applying for."
    },
    {
      step: "04",
      title: "Team Interviews",
      description: "Meet with your potential teammates and leadership to explore how we might work together."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -z-10 w-1/3 h-1/3 bg-primary-100/50 rounded-bl-full opacity-70"></div> {/* Adjusted opacity */}
        <div className="absolute bottom-0 left-0 -z-10 w-1/4 h-1/4 bg-secondary-100/50 rounded-tr-full opacity-70"></div> {/* Adjusted opacity */}

        {/* Floating decorative elements */}
        <motion.div
          className="absolute right-1/4 top-1/5 hidden md:block"
          animate={{
            y: [0, -15, 0],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <div className="w-16 h-16 border-2 border-primary-500/30 rounded-lg rotate-12"></div>
        </motion.div>

        <motion.div
          className="absolute left-1/4 bottom-1/4 hidden md:block"
          animate={{
            y: [0, 15, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            repeatType: "reverse",
            delay: 1
          }}
        >
          <div className="w-12 h-12 border-2 border-secondary-500/30 rounded-full"></div>
        </motion.div>

        <div className="container-padding mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <motion.span
              className="inline-block px-4 py-2 bg-primary-50 text-primary-600 rounded-full text-sm font-medium mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Join Our Team
            </motion.span>

            <motion.h1
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Build the Future of <span className="gradient-text">Legal Technology</span> with Us
            </motion.h1>

            <motion.p
              className="text-xl text-dark-600 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Join a team of innovators and experts dedicated to transforming the legal industry
              through cutting-edge AI solutions. Be part of something meaningful.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Button
                variant="primary"
                size="lg"
                href="#current-openings" // Use href for same-page anchor links
                className="shadow-lg relative overflow-hidden group"
              >
                <span className="relative z-10">View Open Positions</span>
                {/* Optional: Hover effect span */}
                <span className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 bg-dark-50" ref={sectionRef1}>
        <div className="container-padding mx-auto">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible1 ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} // Corrected braces and using isVisible1
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              What Drives Us Forward
            </h2>
            <p className="text-dark-500 text-lg">
              Our core values inform everything we do, from how we build our products to
              how we collaborate as a team.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible1 ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} // Use isVisible1
                transition={{ duration: 0.5, delay: index * 0.1 + 0.1 }}
              >
                <ValueCard
                  icon={value.icon}
                  title={value.title}
                  description={value.description}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-primary-50 relative overflow-hidden" ref={sectionRef2}>
        {/* Background elements */}
        <div className="absolute inset-0 opacity-5 text-primary-200"> {/* Adjusted color */}
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern-benefits" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern-benefits)" />
          </svg>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-primary-600/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-secondary-600/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="container-padding mx-auto relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible2 ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} // Use isVisible2
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Benefits & Perks</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              We Take Care of Our Team
            </h2>
            <p className="text-dark-500 text-lg">
              At Veritect.ai, we believe in providing comprehensive benefits that support your
              professional growth, personal wellbeing, and work-life balance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible2 ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} // Use isVisible2
                transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
              >
                <BenefitItem
                  icon={benefit.icon}
                  title={benefit.title}
                  description={benefit.description}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Openings Section */}
      <section id="current-openings" className="py-20 bg-white" ref={sectionRef3}>
        <div className="container-padding mx-auto">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible3 ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} // Use isVisible3
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Join Our Team</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Current Openings
            </h2>
            <p className="text-dark-500 text-lg">
              Find your next opportunity at Veritect.ai and help shape the future of legal technology.
            </p>
          </motion.div>

          <div className="space-y-4 max-w-5xl mx-auto">
            {jobOpenings.map((job, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible3 ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} // Use isVisible3
                transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
              >
                <JobListing
                  title={job.title}
                  location={job.location}
                  type={job.type}
                  department={job.department}
                  isNew={job.isNew}
                />
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible3 ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} // Use isVisible3
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <p className="text-dark-500 mb-6">
              Don't see the perfect fit? We're always looking for exceptional talent.
            </p>
            <Button
              variant="outline"
              to="/contact" // Use 'to' for React Router links
              className="relative overflow-hidden group"
            >
              <span className="relative z-10">Contact Us</span>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 bg-primary-50 relative overflow-hidden" ref={sectionRef4}>
         <div className="absolute inset-0 opacity-5 text-primary-200"> {/* Adjusted color */}
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              {/* Using a different ID to avoid conflicts if styles differ */}
              <pattern id="grid-pattern-process" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern-process)" />
          </svg>
        </div>

        <div className="container-padding mx-auto relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible4 ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} // Use isVisible4
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Application Process</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              How to Join Our Team
            </h2>
            <p className="text-dark-500 text-lg">
              Our hiring process is designed to be thorough yet efficient, allowing both you and us
              to determine if there's a great mutual fit.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-x-8 gap-y-12 relative">
              {/* Connector line container for medium screens and up */}
               <div className="absolute hidden md:block top-6 left-0 right-0 h-0.5 bg-primary-200 z-0" style={{ width: 'calc(100% - (100% / 4))', left: 'calc(100% / 8)' }}></div>

              {applicationProcess.map((stage, index) => (
                <motion.div
                  key={index}
                  className="relative z-10" // Ensure steps are above the line
                  initial={{ opacity: 0, y: 30 }}
                  animate={isVisible4 ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} // Use isVisible4
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                >
                  {/* Removed individual connector lines, using one background line */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-600 text-white text-xl font-bold mb-4 relative bg-white border-2 border-primary-600"> {/* Ensure circle bg is solid */}
                       <span className="absolute -top-0.5 -left-0.5 -right-0.5 -bottom-0.5 rounded-full bg-primary-600 z-0"></span> {/* Outer color circle */}
                       <span className="relative z-10 text-white">{stage.step}</span> {/* Step number */}
                    </div>
                    <h3 className="text-xl font-bold mb-2">{stage.title}</h3>
                    <p className="text-dark-500 text-sm">{stage.description}</p> {/* Slightly smaller text */}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <div ref={sectionRef5}> {/* Attach ref to trigger animation */}
        <ModernCtaSection
          title="Ready to Make an Impact?"
          description="Join our team of innovators and help shape the future of legal technology with cutting-edge AI solutions."
          buttonText="View Open Positions"
          buttonLink="#current-openings" // Use anchor link
          // Optional: Add animation props if ModernCtaSection doesn't handle it internally
          // initial={{ opacity: 0, y: 30 }}
          // animate={isVisible5 ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          // transition={{ duration: 0.7 }}
        />
       </div>
    </motion.div>
  );
};

export default Careers;